// src/components/CompetitionsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MenuLayout from "./MenuLayout";
import "../styles/CompetitionsPage.css";
import { getCompetitionTypes } from "../api/competition";
import { createCartItem } from "../api/cart";

const tabOptions = [
  { key: "weekly", label: "مسابقات هفتگی" },
  { key: "monthly", label: "مسابقات ماهانه" },
];

const formatMoney = (val) => {
  const num = Number(val || 0);
  if (Number.isNaN(num)) return "-";
  return `$${num.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
};

const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("fa-IR");
};

const CompetitionsPage = () => {
  const [activeTab, setActiveTab] = useState("weekly");
  const [competitions, setCompetitions] = useState([]);
  const [joinMessage, setJoinMessage] = useState("");
  const [joinLoading, setJoinLoading] = useState(false);
  const navigate = useNavigate();
  const isWeekly = activeTab === "weekly";

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getCompetitionTypes();
        setCompetitions(Array.isArray(res?.competitions) ? res.competitions : []);
      } catch {
        setCompetitions([]);
      }
    };
    load();
  }, []);

  const activeCompetition = useMemo(() => {
    const type = isWeekly ? "weekly" : "monthly";
    const sameType = competitions.filter((c) => c.type === type);
    return sameType.find((c) => c.is_active) || sameType[0] || null;
  }, [competitions, isWeekly]);

  const cards = useMemo(
    () =>
      isWeekly
        ? [
            { id: "gold", title: "نفر اول", prize: "$100", medal: "/images/competitions/medal-gold.svg" },
            { id: "silver", title: "نفر دوم", prize: "$50", medal: "/images/competitions/medal-silver.svg" },
            { id: "bronze", title: "نفر سوم", prize: "$20", medal: "/images/competitions/medal-bronze.svg" },
          ]
        : [
            { id: "gold", title: "نفر اول", prize: "$1000", medal: "/images/competitions/medal-gold.svg" },
            { id: "silver", title: "نفر دوم", prize: "$500", medal: "/images/competitions/medal-silver.svg" },
            { id: "bronze", title: "نفر سوم", prize: "$200", medal: "/images/competitions/medal-bronze.svg" },
          ],
    [isWeekly]
  );

  const entryFee = activeCompetition ? Number(activeCompetition.entry_fee || 0) : 0;
  const entryFeeLabel = entryFee > 0 ? formatMoney(entryFee) : "رایگان";
  const profitTarget = activeCompetition?.profit_target || "بیشترین درصد سود تا پایان هفته";
  const isJoined = Boolean(activeCompetition?.is_joined);

  const timelineItems = useMemo(
    () => [
      { id: "goal", label: "هدف", value: profitTarget },
      { id: "cost", label: "هزینه", value: entryFeeLabel },
      { id: "start", label: "شروع محاسبه سود", value: formatDate(activeCompetition?.scoring_start || activeCompetition?.start_time) },
      { id: "end", label: "پایان محاسبه سود", value: formatDate(activeCompetition?.scoring_end || activeCompetition?.end_time) },
    ],
    [activeCompetition, entryFeeLabel, profitTarget]
  );

  const handleJoinCompetition = async () => {
    if (joinLoading) return;
    if (!activeCompetition) {
      setJoinMessage("مسابقه‌ای فعال پیدا نشد.");
      return;
    }
    if (isJoined) {
      setJoinMessage(activeCompetition.type === "weekly" ? "عضویت هفتگی شما دائمی و فعال است." : "در مسابقه ماهانه جاری عضو هستید.");
      return;
    }
    setJoinLoading(true);
    setJoinMessage("");
    try {
      await createCartItem({
        item_type: "competition",
        name: activeCompetition.name,
        market_type: "comp",
        investment_amount: entryFee,
        price: entryFee,
        platform_type: "comp",
        broker_type: "0",
      });
      navigate("/cart");
    } catch (err) {
      setJoinMessage(err?.message || "خطا در افزودن مسابقه به سبد خرید");
    } finally {
      setJoinLoading(false);
    }
  };

  return (
    <MenuLayout>
      <div className="comp-landing">
        <header className="comp-landing__header">
          <div className="comp-landing__title">مسابقات</div>
          <p className="comp-landing__subtitle">
            در مسابقات شرکت کنید و با عملکرد بهتر، جوایز ویژه دریافت کنید.
          </p>
          <div className="comp-landing__tabs">
            {tabOptions.map((tab) => (
              <button
                key={tab.key}
                className={`comp-landing__tab ${activeTab === tab.key ? "is-active" : ""}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </header>

        <section className="comp-landing__cards">
          {cards.map((card) => (
            <div key={card.id} className="comp-card">
              <img src={card.medal} alt={card.title} className="comp-card__medal" />
              <div className="comp-card__title">{card.title}</div>
              <div className="comp-card__price">{card.prize}</div>
              <div className="comp-card__label">جایزه</div>
            </div>
          ))}
        </section>

        <section className="comp-landing__timeline">
          <div className="comp-timeline__header">
            <span className="comp-timeline__title">جزئیات مسابقه</span>
          </div>
          <div className="comp-timeline__body">
            {timelineItems.map((item, idx) => (
              <div key={item.id} className="comp-timeline__item">
                <span className="comp-timeline__dot" />
                <div className="comp-timeline__content">
                  <div className="comp-timeline__label">{item.label}</div>
                  <div className="comp-timeline__value">{item.value}</div>
                </div>
                {idx < timelineItems.length - 1 && <span className="comp-timeline__divider" />}
              </div>
            ))}
          </div>
        </section>

        <section className="comp-landing__rules" aria-label="قوانین مسابقات">
          <h2>شیوه محاسبه رتبه</h2>
          <p>رتبه بر اساس مجموع سود همه حساب‌های چالش و Real شما محاسبه می‌شود؛ حساب جداگانه‌ای برای مسابقه ساخته نمی‌شود.</p>
          <ul>
            <li>هفتگی: سود معاملات از دوشنبه تا پایان جمعه محاسبه می‌شود و عضویت تا زمان حذف توسط پشتیبانی یا نقض قوانین باقی می‌ماند.</li>
            <li>ماهانه: سود از ابتدای ماه میلادی تا پایان همان ماه محاسبه می‌شود و برای ماه بعد باید دوباره ثبت‌نام کنید.</li>
            <li>نقض قوانین هر حساب، از جمله حد افت معاملات باز، باعث حذف از مسابقه می‌شود.</li>
          </ul>
        </section>

        <div className="comp-landing__actions">
          <button className="comp-btn comp-btn--ghost" onClick={() => navigate("/competitions/current")}>
            بررسی مسابقه جاری
          </button>
          <button className="comp-btn comp-btn--accent" onClick={() => navigate("/competitions/mine")}>
            مسابقات من
          </button>
          <button className="comp-btn comp-btn--primary" onClick={handleJoinCompetition} disabled={joinLoading || !activeCompetition || isJoined}>{joinLoading ? "در حال انتقال..." : isJoined ? "عضویت فعال است" : "شرکت در مسابقه!"}</button>
        </div>
        {joinMessage && <div className="comp-join-message">{joinMessage}</div>}
      </div>
    </MenuLayout>
  );
};

export default CompetitionsPage;
